import { getInstructionsLink } from '@anm/components/stream/destination_management/helpers';
import { ErrorNetwork } from '@anm/components/stream/destination_management/types';
import browserStore from '@anm/helpers/browserStore';
import parseDestinationExtra from '@anm/helpers/stream/parseDestinationExtra';
import { userDestinationsActions, userDestinationsSelectors } from '@anm/store/modules/userDestinations';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import getDestinationStorageKey from '../helpers/getDestinationStorageKey';

import { DestinationParams } from './../types';

type HookProps = {
  waveUrl: string;
  isInModal: boolean;
  onRedirect?: (link: string) => void;
};

const YOUTUBE_NO_CHANNEL_ERROR = 'no_youtube_channels';
const YOUTUBE_NOT_VERIFIED_ERROR = 'youtube_channel_not_verified';

export const ERROR_NOTIFICATION_TIMEOUT = 30000;

const useShowError = ({ isInModal, waveUrl, onRedirect }: HookProps) => {
  const storageKey = getDestinationStorageKey();

  const [errorNetwork, setErrorNetwork] = useState<ErrorNetwork>();

  const redirectToInstructions = (errorNetwork: ErrorNetwork) => {
    const link = getInstructionsLink({ waveUrl, network: errorNetwork });
    onRedirect?.(link);
  };

  const clearError = () => browserStore.remove(storageKey);

  const closeNotification = () => {
    clearError();
    setErrorNetwork(undefined);
  };

  useEffect(() => {
    if (!errorNetwork) return;

    if (!isInModal && onRedirect) {
      redirectToInstructions(errorNetwork);
      return;
    }
    const timer = setTimeout(() => closeNotification(), ERROR_NOTIFICATION_TIMEOUT);

    return () => clearTimeout(timer);
  }, [errorNetwork]);

  const dispatch = useDispatch();
  const params = browserStore.get(storageKey) as DestinationParams;
  const allDestinations = useSelector(userDestinationsSelectors.selectDestinationsList);

  const canCheckErrors = useSelector(userDestinationsSelectors.selectCanCheckErrors);

  useEffect(() => {
    if (!canCheckErrors) return;

    const enabledLinkedinDestinations = allDestinations?.filter(d => d.network === 'LINKEDIN' && d.enabled);

    const notAllowedDestination = enabledLinkedinDestinations?.find(d => !parseDestinationExtra(d)['allowed']);

    if (notAllowedDestination) {
      dispatch(userDestinationsActions.toggleSocialDestinations({ [notAllowedDestination.id]: false }));
      setErrorNetwork('LINKEDIN');
      return;
    }

    const facebookDestinationWithError = allDestinations?.find(
      d => d.network === 'FACEBOOK' && d.error && d.error !== 'SHOULD_RECONNECT'
    );

    if (facebookDestinationWithError) {
      setErrorNetwork('FACEBOOK');
    }
  }, [allDestinations, canCheckErrors]);

  useEffect(() => {
    if (!params?.network || !canCheckErrors) return;

    const isYoutubeAdded = params.network === 'youtube';

    if (!isYoutubeAdded) return;

    const isNoChannelError = params.error === YOUTUBE_NO_CHANNEL_ERROR;
    const isNotVerifiedError = params.error === YOUTUBE_NOT_VERIFIED_ERROR;
    const isError = isNoChannelError || isNotVerifiedError;

    if (isError) {
      setErrorNetwork(isNotVerifiedError ? 'YOUTUBE_NOT_VERIFIED' : 'YOUTUBE_NO_CHANNEL');
    }
  }, [params?.network, canCheckErrors]);

  return [errorNetwork, closeNotification] as const;
};

export default useShowError;
